import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import {
  getClassesBySchoolId,
  getSharedTeachersByClassId,
  getTeachersBySchoolId,
  removeFromSharedClass,
  shareClass
} from "../services/TeacherAPIClient";
import { getClassAcademicYear, getClassGradeLabel } from "../utils/classDisplay";
import "./TeacherRemoveColleagueAccessModal.css";

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const normalisePrefill = (prefill) => ({
  className: String(prefill?.className || prefill?.class_name || "").trim(),
  teacherName: String(
    prefill?.teacherName ||
    prefill?.teacher_name ||
    ""
  ).trim(),
  secondTeacherName: String(
    prefill?.secondTeacherName ||
    prefill?.second_teacher_name ||
    ""
  ).trim()
});

const normaliseClass = (classItem, countryGrades) => ({
  ...classItem,
  id: classItem?.id || classItem?._id,
  name: classItem?.name || classItem?.className || "Unnamed class",
  grade: getClassGradeLabel(classItem, countryGrades),
  academicYear: getClassAcademicYear(classItem),
  teacherId: classItem?.teacherId || classItem?.teacher_id || classItem?.teacherid,
  classOwnerId: classItem?.classOwnerId || classItem?.class_owner_id || classItem?.classownerid
});

const normaliseTeacher = (teacher) => {
  const teacherData = teacher && typeof teacher === "object" ? teacher : {};

  return {
    ...teacherData,
    id:
      typeof teacher === "string"
        ? teacher
        : teacherData.id || teacherData._id || teacherData.teacherid || teacherData.teacherId || teacherData.teacher_id,
    name: teacherData.name || teacherData.fullName || teacherData.displayName || "Unnamed teacher",
    email: teacherData.email || "",
    role: teacherData.role || teacherData.roleLabel || ""
  };
};

const normaliseSharedTeacher = (item, teachersById) => {
  const teacherId =
    typeof item === "string"
      ? item
      : item?.teacherId || item?.teacher_id || item?.teacher?.id || item?.teacher?._id || item?.id || item?._id;
  const knownTeacher = teachersById.get(teacherId);

  return normaliseTeacher(knownTeacher || item?.teacher || item);
};

const findSingleExactMatch = (items, query, getValue) => {
  const target = normaliseText(query);

  if (!target) {
    return null;
  }

  const matches = items.filter((item) => normaliseText(getValue(item)) === target);
  return matches.length === 1 ? matches[0] : null;
};

function TeacherRemoveColleagueAccessModal({
  show,
  handleClose,
  schoolid,
  countryGrades = null,
  currentTeacherId = null,
  prefill = null,
  onRequestAccessForTeacher = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("form");
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [sharedTeachers, setSharedTeachers] = useState([]);
  const [classSearchTerm, setClassSearchTerm] = useState("");
  const [teacherSearchTerm, setTeacherSearchTerm] = useState("");
  const [replacementSearchTerm, setReplacementSearchTerm] = useState("");
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [replacementTeacher, setReplacementTeacher] = useState(null);
  const [removedTeacher, setRemovedTeacher] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSharedTeachers, setIsLoadingSharedTeachers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const prefillContext = useMemo(() => normalisePrefill(prefill), [prefill]);

  const filteredClasses = useMemo(() => {
    const query = normaliseText(classSearchTerm);
    if (!query) return [];

    return classes.filter((classItem) =>
      [classItem.name, classItem.grade, classItem.academicYear].some((value) =>
        normaliseText(value).includes(query)
      )
    );
  }, [classes, classSearchTerm]);

  const filteredSharedTeachers = useMemo(() => {
    const query = normaliseText(teacherSearchTerm);
    if (!query) return sharedTeachers;

    return sharedTeachers.filter((teacher) =>
      [teacher.name, teacher.email, teacher.role].some((value) =>
        normaliseText(value).includes(query)
      )
    );
  }, [sharedTeachers, teacherSearchTerm]);

  const replacementOptions = useMemo(() => {
    const sharedIds = new Set(sharedTeachers.map((teacher) => teacher.id));
    const query = normaliseText(replacementSearchTerm);

    return teachers
      .filter((teacher) => teacher.id !== selectedTeacher?.id)
      .filter((teacher) => !sharedIds.has(teacher.id))
      .filter((teacher) => !query || [teacher.name, teacher.email].some((value) => normaliseText(value).includes(query)));
  }, [replacementSearchTerm, selectedTeacher, sharedTeachers, teachers]);

  const classTeacherIdsAfterRemoval = useMemo(() => {
    if (!selectedClass || !selectedTeacher) {
      return new Set();
    }

    const teacherIds = new Set(
      [
        selectedClass.teacherId,
        selectedClass.classOwnerId,
        ...sharedTeachers.map((teacher) => teacher.id)
      ].filter(Boolean)
    );

    if (
      currentTeacherId &&
      (selectedClass.teacherId === currentTeacherId ||
        selectedClass.classOwnerId === currentTeacherId ||
        sharedTeachers.some((teacher) => teacher.id === currentTeacherId))
    ) {
      teacherIds.add(currentTeacherId);
    }

    teacherIds.delete(selectedTeacher.id);
    return teacherIds;
  }, [currentTeacherId, selectedClass, selectedTeacher, sharedTeachers]);

  const needsReplacement = Boolean(selectedClass && selectedTeacher && classTeacherIdsAfterRemoval.size < 1);
  const canSubmit = Boolean(selectedClass && selectedTeacher && (!needsReplacement || replacementTeacher) && !isSubmitting);

  useEffect(() => {
    if (!show) {
      return;
    }

    setStage("form");
    setClasses([]);
    setTeachers([]);
    setSharedTeachers([]);
    setClassSearchTerm(prefillContext.className);
    setTeacherSearchTerm(prefillContext.teacherName);
    setReplacementSearchTerm(prefillContext.secondTeacherName);
    setSelectedClass(null);
    setSelectedTeacher(null);
    setReplacementTeacher(null);
    setRemovedTeacher(null);
    setError("");

    if (!schoolid) {
      setError("Classes and teachers could not be loaded. Please try again.");
      return;
    }

    setIsLoading(true);
    Promise.all([getClassesBySchoolId(schoolid), getTeachersBySchoolId(schoolid)])
      .then(([classesResponse, teachersResponse]) => {
        const normalisedClasses = (Array.isArray(classesResponse) ? classesResponse : [])
          .map((classItem) => normaliseClass(classItem, countryGrades))
          .filter((classItem) => classItem.id && classItem.active !== false)
          .filter((classItem) => !classItem.snapshot && !classItem.trial_class)
          .sort((firstClass, secondClass) => firstClass.name.localeCompare(secondClass.name));
        const normalisedTeachers = (Array.isArray(teachersResponse) ? teachersResponse : [])
          .map(normaliseTeacher)
          .filter((teacher) => teacher.id && teacher.active !== false)
          .sort((firstTeacher, secondTeacher) => firstTeacher.name.localeCompare(secondTeacher.name));

        setClasses(normalisedClasses);
        setTeachers(normalisedTeachers);

        const prefilledClass = findSingleExactMatch(normalisedClasses, prefillContext.className, (classItem) => classItem.name);
        if (prefilledClass) {
          setSelectedClass(prefilledClass);
        }
      })
      .catch((loadError) => {
        console.error("Error loading remove colleague access data:", loadError);
        setError("Classes and teachers could not be loaded. Please try again.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [show, schoolid, countryGrades, prefillContext]);

  useEffect(() => {
    if (!show || !selectedClass) {
      setSharedTeachers([]);
      setSelectedTeacher(null);
      return;
    }

    setIsLoadingSharedTeachers(true);
    setError("");

    const teachersById = new Map(teachers.map((teacher) => [teacher.id, teacher]));

    getSharedTeachersByClassId(selectedClass.id)
      .then((sharedResponse) => {
        const normalisedSharedTeachers = (Array.isArray(sharedResponse) ? sharedResponse : [])
          .map((item) => normaliseSharedTeacher(item, teachersById))
          .filter((teacher) => teacher.id)
          .sort((firstTeacher, secondTeacher) => firstTeacher.name.localeCompare(secondTeacher.name));

        setSharedTeachers(normalisedSharedTeachers);

        const prefilledTeacher = findSingleExactMatch(
          normalisedSharedTeachers,
          prefillContext.teacherName,
          (teacher) => teacher.name
        );

        setSelectedTeacher(prefilledTeacher);
      })
      .catch((sharedError) => {
        console.error("Error loading class colleagues:", sharedError);
        setSharedTeachers([]);
        setError("Colleagues for this class could not be loaded. Please try again.");
      })
      .finally(() => {
        setIsLoadingSharedTeachers(false);
      });
  }, [show, selectedClass, teachers, prefillContext.teacherName]);

  useEffect(() => {
    if (!show || !needsReplacement || replacementTeacher || replacementOptions.length < 1) {
      return;
    }

    const prefilledReplacement = findSingleExactMatch(
      replacementOptions,
      prefillContext.secondTeacherName,
      (teacher) => teacher.name
    );

    if (prefilledReplacement) {
      setReplacementTeacher(prefilledReplacement);
    }
  }, [show, needsReplacement, replacementOptions, replacementTeacher, prefillContext.secondTeacherName]);

  const handleClassSelect = (classItem) => {
    setSelectedClass(classItem);
    setClassSearchTerm(classItem.name);
    setSelectedTeacher(null);
    setReplacementTeacher(null);
    setError("");
  };

  const handleClassSearchChange = (event) => {
    const nextSearchTerm = event.target.value;
    setClassSearchTerm(nextSearchTerm);
    setError("");

    if (
      selectedClass &&
      normaliseText(nextSearchTerm) !== normaliseText(selectedClass.name)
    ) {
      setSelectedClass(null);
      setSelectedTeacher(null);
      setReplacementTeacher(null);
      setSharedTeachers([]);
    }
  };

  const handleTeacherSelect = (teacher) => {
    setSelectedTeacher(teacher);
    setTeacherSearchTerm(teacher.name);
    setReplacementTeacher(null);
    setError("");
  };

  const handleTeacherSearchChange = (event) => {
    const nextSearchTerm = event.target.value;
    setTeacherSearchTerm(nextSearchTerm);
    setError("");

    if (
      selectedTeacher &&
      normaliseText(nextSearchTerm) !== normaliseText(selectedTeacher.name)
    ) {
      setSelectedTeacher(null);
      setReplacementTeacher(null);
    }
  };

  const handleRemove = async () => {
    if (needsReplacement && stage !== "replacement") {
      setStage("replacement");
      return;
    }

    if (!canSubmit) {
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      if (needsReplacement && replacementTeacher) {
        await shareClass(selectedClass.id, replacementTeacher.id);
      }

      await removeFromSharedClass(selectedClass.id, selectedTeacher.id);
      setRemovedTeacher(selectedTeacher);
      setStage("success");
    } catch (submitError) {
      console.error("Error removing colleague access:", submitError);
      setError("The colleague could not be removed from this class. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForAnother = () => {
    setStage("form");
    setClassSearchTerm("");
    setTeacherSearchTerm("");
    setReplacementSearchTerm("");
    setSelectedClass(null);
    setSelectedTeacher(null);
    setReplacementTeacher(null);
    setSharedTeachers([]);
    setRemovedTeacher(null);
    setError("");
  };

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: selectedClass?.name || removedTeacher?.name || "",
        message: "Colleague access removed."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderClassResults = () => {
    if (isLoading) {
      return (
        <div className="remove-colleague-search-state">
          <Spinner animation="border" size="sm" />
          <span>Loading classes...</span>
        </div>
      );
    }

    if (!classSearchTerm.trim()) {
      return <p className="remove-colleague-muted">Start typing the class name.</p>;
    }

    if (selectedClass) {
      return (
        <button
          key={selectedClass.id}
          type="button"
          className="remove-colleague-result selected"
          onClick={() => handleClassSelect(selectedClass)}
          aria-label={`Select ${selectedClass.name}`}
          aria-pressed
        >
          <strong>{selectedClass.name}</strong>
          <span>{selectedClass.grade || selectedClass.academicYear || "Class details unavailable"}</span>
          <span className="remove-colleague-selected-label">Selected</span>
        </button>
      );
    }

    if (filteredClasses.length < 1) {
      return <p className="remove-colleague-muted">No matching classes found.</p>;
    }

    return filteredClasses.map((classItem) => {
      const selected = selectedClass?.id === classItem.id;

      return (
        <button
          key={classItem.id}
          type="button"
          className={`remove-colleague-result${selected ? " selected" : ""}`}
          onClick={() => handleClassSelect(classItem)}
          aria-label={`Select ${classItem.name}`}
          aria-pressed={selected}
        >
          <strong>{classItem.name}</strong>
          <span>{classItem.grade || classItem.academicYear || "Class details unavailable"}</span>
          {selected && <span className="remove-colleague-selected-label">Selected</span>}
        </button>
      );
    });
  };

  const renderTeacherResults = () => {
    if (!selectedClass) {
      return <p className="remove-colleague-muted">Choose the class first.</p>;
    }

    if (isLoadingSharedTeachers) {
      return (
        <div className="remove-colleague-search-state">
          <Spinner animation="border" size="sm" />
          <span>Loading colleagues...</span>
        </div>
      );
    }

    if (filteredSharedTeachers.length < 1) {
      return <p className="remove-colleague-muted">No current colleagues found for this class.</p>;
    }

    if (selectedTeacher) {
      return (
        <button
          key={selectedTeacher.id}
          type="button"
          className="remove-colleague-result selected"
          onClick={() => handleTeacherSelect(selectedTeacher)}
          aria-label={`Select ${selectedTeacher.name}`}
          aria-pressed
        >
          <strong>{selectedTeacher.name}</strong>
          <span>Can currently use {selectedClass.name}.</span>
          <span className="remove-colleague-selected-label">Selected</span>
        </button>
      );
    }

    return filteredSharedTeachers.map((teacher) => {
      const selected = selectedTeacher?.id === teacher.id;

      return (
        <button
          key={teacher.id}
          type="button"
          className={`remove-colleague-result${selected ? " selected" : ""}`}
          onClick={() => handleTeacherSelect(teacher)}
          aria-label={`Select ${teacher.name}`}
          aria-pressed={selected}
        >
          <strong>{teacher.name}</strong>
          <span>Can currently use {selectedClass.name}.</span>
          {selected && <span className="remove-colleague-selected-label">Selected</span>}
        </button>
      );
    });
  };

  const renderReplacementResults = () => {
    if (!replacementSearchTerm.trim()) {
      return <p className="remove-colleague-muted">Start typing who should use this class.</p>;
    }

    if (replacementOptions.length < 1) {
      return <p className="remove-colleague-muted">No matching teachers found.</p>;
    }

    return replacementOptions.map((teacher) => {
      const selected = replacementTeacher?.id === teacher.id;

      return (
        <button
          key={teacher.id}
          type="button"
          className={`remove-colleague-result${selected ? " selected" : ""}`}
          onClick={() => {
            setReplacementTeacher(teacher);
            setError("");
          }}
          aria-label={`Select ${teacher.name}`}
          aria-pressed={selected}
        >
          <strong>{teacher.name}</strong>
          <span>Will be added to {selectedClass?.name} before {selectedTeacher?.name} is removed.</span>
          {selected && <span className="remove-colleague-selected-label">Selected</span>}
        </button>
      );
    });
  };

  const renderForm = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Remove Colleague Access</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="remove-colleague-intro">Choose the class and colleague.</p>
        {error && <Alert variant="danger">{error}</Alert>}
        <div className="remove-colleague-grid">
          <div>
            <Form.Group controlId="teacherRemoveColleagueClass">
              <Form.Label>Which class?</Form.Label>
              <Form.Text as="div" className="remove-colleague-field-label">Class name</Form.Text>
              <Form.Control
                type="search"
                value={classSearchTerm}
                onChange={handleClassSearchChange}
              />
            </Form.Group>
            <div className="remove-colleague-results">{renderClassResults()}</div>
          </div>
          <div>
            <Form.Group controlId="teacherRemoveColleagueTeacher">
              <Form.Label>Who should be removed?</Form.Label>
              <Form.Text as="div" className="remove-colleague-field-label">Teacher name</Form.Text>
              <Form.Control
                type="search"
                value={teacherSearchTerm}
                onChange={handleTeacherSearchChange}
                disabled={!selectedClass}
              />
            </Form.Group>
            <div className="remove-colleague-results">{renderTeacherResults()}</div>
          </div>
        </div>
        {selectedClass && selectedTeacher && (
          <div className="remove-colleague-note">
            {selectedTeacher.name} will be removed from {selectedClass.name} only. {selectedClass.name} and pupils stay unchanged.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <span className="remove-colleague-footer-note">
          {selectedClass && selectedTeacher
            ? `${selectedClass.name}, ${selectedTeacher.name} selected.`
            : "Choose a class and colleague."}
        </span>
        <Button variant="outline-secondary" onClick={handleClose}>Cancel</Button>
        <Button variant="danger" onClick={handleRemove} disabled={!selectedClass || !selectedTeacher || isSubmitting}>
          {needsReplacement ? "Next" : isSubmitting ? "Removing..." : "Remove from class"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderReplacement = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Choose Someone Else First</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="remove-colleague-intro">{selectedClass?.name} needs another colleague before {selectedTeacher?.name?.split(" ")[0]} is removed.</p>
        {error && <Alert variant="danger">{error}</Alert>}
        <Alert variant="warning" className="remove-colleague-warning">
          <FaExclamationCircle aria-hidden="true" />
          <span>
            <strong>{selectedTeacher?.name} is the only colleague using {selectedClass?.name}.</strong>
            <span>Add another colleague to {selectedClass?.name} before removing {selectedTeacher?.name?.split(" ")[0]}.</span>
          </span>
        </Alert>
        <Form.Group controlId="teacherRemoveColleagueReplacement">
          <Form.Label>Add colleague to {selectedClass?.name}</Form.Label>
          <Form.Text as="div" className="remove-colleague-field-label">Teacher name</Form.Text>
          <Form.Control
            type="search"
            value={replacementSearchTerm}
            onChange={(event) => {
              setReplacementSearchTerm(event.target.value);
              setReplacementTeacher(null);
              setError("");
            }}
          />
        </Form.Group>
        <div className="remove-colleague-results">{renderReplacementResults()}</div>
      </Modal.Body>
      <Modal.Footer>
        <span className="remove-colleague-footer-note">
          {replacementTeacher ? `${replacementTeacher.name} selected.` : "Choose another colleague."}
        </span>
        <Button variant="outline-secondary" onClick={() => setStage("form")}>Back</Button>
        <Button variant="danger" onClick={handleRemove} disabled={!canSubmit}>
          {isSubmitting ? "Removing..." : "Remove from class"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccess = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Colleague Removed</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="remove-colleague-intro">{selectedClass?.name} has been updated.</p>
        <Alert variant="success" className="remove-colleague-success">
          <FaCheckCircle aria-hidden="true" />
          <span>
            <strong>{removedTeacher?.name} can no longer use {selectedClass?.name}.</strong>
            <span>{selectedClass?.name}, pupils, and assessment results were kept.</span>
          </span>
        </Alert>
        <div className="remove-colleague-summary">
          <strong>What happened</strong>
          <span className="remove-colleague-row">
            <span>Removed</span>
            <span>{removedTeacher?.name} from {selectedClass?.name}</span>
          </span>
          <span className="remove-colleague-row">
            <span>Kept</span>
            <span>{selectedClass?.name}, pupils, assessment results, and {removedTeacher?.name?.split(" ")[0]}'s other classes.</span>
          </span>
        </div>
      </Modal.Body>
      <Modal.Footer className="remove-colleague-success-footer">
        <span className="remove-colleague-footer-note">
          {embedded ? "Done returns to SENSAI." : `${selectedClass?.name} updated.`}
        </span>
        <Button variant="outline-secondary" onClick={resetForAnother}>Remove another colleague</Button>
        {onRequestAccessForTeacher && (
          <Button
            variant="outline-secondary"
            onClick={() => onRequestAccessForTeacher({
              className: selectedClass?.name,
              teacherName: replacementTeacher?.name
            })}
          >
            Request access for teacher
          </Button>
        )}
        <Button variant="primary" onClick={handleDone}>Done</Button>
      </Modal.Footer>
    </>
  );

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      backdrop="static"
      className="teacher-remove-colleague-modal"
    >
      {stage === "success" ? renderSuccess() : stage === "replacement" ? renderReplacement() : renderForm()}
    </Modal>
  );
}

export default TeacherRemoveColleagueAccessModal;
